import { useRef, useState } from 'react';
import type { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Avatar, Box, Button, ButtonBase, Divider, ListItemIcon, ListItemText, MenuItem, MenuList,
  Popover, styled, Tooltip, Typography, useTheme } from '@mui/material';
import UserIcon from '../../icons/User';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useLogout } from 'ra-core';
import logoBlue from '~/assets/logo_blue.png'
import logoWhite from '~/assets/logo_transparent.png'
import { useEffect } from 'react';
import { getUserProfile } from '~/reducers/userProfile';
import { crudGetOneEx } from '~/actions/crudFetchEx';
import { useResourceRegister } from '~/utils/resourceRegister';
import Create from '~/controller/Create';
import WithSave from '~/controller/WithSave';
import { useIsSecureFileOnly } from '~/authProvider/useIsSecureFileOnly';
import useIdTokenUser from '~/authProvider/useIdTokenUser';



const AccountPortrait = styled(ButtonBase)(({theme}) => ({
  alignItems: 'center',
  display: 'flex',
  borderRadius: '50%',
  padding: theme.spacing(1),
  margin: theme.spacing(-1),
}))


const AccountPopover: FC = () => {
  useResourceRegister({ name: 'sso-logout' })
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const logout = useLogout()
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState<boolean>(false);
  const theme = useTheme()
  const dispatch = useDispatch()
  const tokenInfo = useIdTokenUser()

  useEffect(() => {
    dispatch(crudGetOneEx('profile', 'edit', ''))
  }, [])

  const user = useSelector(getUserProfile)
  const isSecureFileOnly = useIsSecureFileOnly()

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = (ssoLogoutInfo:any) => {
    const logoutUrl : string|undefined = ssoLogoutInfo?.data?.url
    if (logoutUrl) {
      setTimeout(() => {
        window.location.assign(logoutUrl)
      })
    }
    try {
      handleClose();
      logout()
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Unable to logout', {
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'top'
        },
        variant: 'error'
      });
    }
  };

  return (
    <>
      <Tooltip title="User Profile">
        <AccountPortrait
          id="account-manage-menu-btn"
          onClick={handleOpen}
          ref={anchorRef}
          focusRipple={true}
          aria-label={`Account manager for ${user?.username}`}
          aria-expanded={open ? 'true' : undefined}
          aria-controls="account-manage-menu"
          aria-haspopup="menu"
        >
          <Avatar
            alt={user?.username || 'user avatar picture'}
            src={user?.avatarUrl || undefined}
            sx={{
              height: 32,
              width: 32
            }}
          />
        </AccountPortrait>
      </Tooltip>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom'
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          id: 'account-manage-menu',
          'aria-labelledby': "account-manage-menu-btn",
          sx: { width: 240 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <img 
            src={theme.palette.mode === 'light' ? logoBlue : logoWhite} 
            alt="OnData logo"
            height="24"
          />
        </Box>
        <Divider />
        <MenuList sx={{ mt: 2 }}>
          <MenuItem
            component={RouterLink}
            to="/profile/edit"
            onClick={handleClose}
          >
            <ListItemIcon>
              <UserIcon sx={{ color: 'primary.light' }} fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={(
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  My Account
                </Typography>
              )}
            />
          </MenuItem>
          {tokenInfo?.idp === 'local' &&
            <MenuItem
              component={RouterLink}
              to="/change-password"
              onClick={handleClose}
            >
              <ListItemIcon>
                <LockOutlinedIcon sx={{ color: 'primary.light' }} fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={(
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                  >
                    Password
                  </Typography>
                )}
              />
            </MenuItem>}
          {!isSecureFileOnly &&
            <MenuItem
              component={RouterLink}
              to="/device"
              onClick={handleClose}
            >
              <ListItemIcon>
                <AppRegistrationIcon sx={{ color: 'primary.light' }} fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={(
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                  >
                    Register Client
                  </Typography>
                )}
              />
            </MenuItem>}
        </MenuList>
        <Box sx={{ p: 2 }}>
          <Create resource="sso-logout" basePath="">
            <WithSave 
              render={({ save }) => (
                <Button
                  color="primary"
                  fullWidth
                  onClick={() => save({}, { onSuccess: handleLogout, onFailure: handleLogout })}
                  variant="outlined"
                >
                  Logout
                </Button>
              )}
            />
          </Create>
        </Box>
      </Popover>
    </>
  );
};

export default AccountPopover;
